// Umami Cloud analytics. The tracker script is injected only when
// REACT_APP_UMAMI_WEBSITE_ID is set (production builds on the VM); dev builds
// ship zero analytics. Every export is a safe no-op when the script is absent
// or blocked by an ad blocker — analytics must never throw.
//
// Checkout returns hard-reload the page (App.js), so events earned during a
// Stripe round-trip are staged in sessionStorage and flushed once the tracker
// script loads after the reload.

const WEBSITE_ID = process.env.REACT_APP_UMAMI_WEBSITE_ID || "";
const FLOW_KEY = "muraja.checkoutFlow";
const PENDING_KEY = "muraja.pendingEvents";

// Inject the tracker script once. data-domains keeps localhost/LAN previews
// silent even if the var leaks into a local build; data-exclude-search keeps
// secrets (?token=, ?session_id=) out of tracked URLs.
export function initAnalytics() {
  if (!WEBSITE_ID) return;
  if (document.querySelector('script[src*="umami"]')) return;
  const script = document.createElement("script");
  script.defer = true;
  script.src = "https://cloud.umami.is/script.js";
  script.setAttribute("data-website-id", WEBSITE_ID);
  script.setAttribute("data-domains", "muraja.app,www.muraja.app,my.muraja.app");
  script.setAttribute("data-exclude-search", "true");
  script.addEventListener("load", flushPendingEvents);
  document.head.appendChild(script);
}

// Send a custom event. Event names and non-identifying labels only — never put
// emails, names, or user IDs in `data`.
export function track(event, data) {
  try {
    if (typeof window.umami?.track === "function") window.umami.track(event, data);
  } catch {
    // Analytics is best-effort; swallow everything.
  }
}

// Remember which flow is leaving for Stripe Checkout, so the return seam can
// tell a resubscribe from an institute checkout.
// Flows: "resubscribe" | "institute".
// (Signup and trial-start no longer leave for Stripe at all — they complete
// in-app and fire their events inline.)
export function setCheckoutFlow(flow) {
  try {
    sessionStorage.setItem(FLOW_KEY, flow);
  } catch {
    // Storage unavailable → the return seam falls back to flow "unknown".
  }
}

// Called at checkout-complete, just before the hard reload: turn the staged
// flow into events and park them for flushPendingEvents on the next load.
export function queueCheckoutEvents() {
  try {
    const flow = sessionStorage.getItem(FLOW_KEY);
    sessionStorage.removeItem(FLOW_KEY);
    let events;
    if (flow === "institute") {
      events = [
        { event: "signup", data: { method: "checkout" } },
        { event: "subscribe", data: { flow: "institute" } },
      ];
    } else {
      events = [{ event: "subscribe", data: { flow: flow || "unknown" } }];
    }
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(events));
  } catch {
    // Best-effort only.
  }
}

function flushPendingEvents() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_KEY);
    for (const { event, data } of JSON.parse(raw)) track(event, data);
  } catch {
    // Corrupt queue or storage unavailable — drop it.
  }
}
